import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import '../report.css';
import ApiService from '../../../../services/apiService';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

const intFmt = (n) =>
  new Intl.NumberFormat('en-GB', {
    maximumFractionDigits: 0,
  }).format(Number.isFinite(+n) ? +n : 0);

const dateToInputValue = (d) => new Date(d).toISOString().slice(0, 10);

const COLUMNS = [
  { key: 'itemNo', label: 'Item No' },
  { key: 'itemDescription', label: 'Item Description' },
  { key: 'soldQty', label: 'Sold Qty', align: 'right' },
];

const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 999999 }),
  menu: (base) => ({ ...base, zIndex: 999999 }),
  control: (base) => ({
    ...base,
    minHeight: 38,
    borderRadius: 10,
  }),
};

const UnSoldItemReport = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalRecords: 0,
    totalPages: 1,
    hasPrevious: false,
    hasNext: false,
  });

  const now = new Date();
  const tenDaysAgo = new Date();
  tenDaysAgo.setDate(now.getDate() - 10);

  const [formData, setFormData] = useState({
    fromDate: dateToInputValue(tenDaysAgo),
    toDate: dateToInputValue(now),
  });

  const [searchText, setSearchText] = useState('');
  const [sortState, setSortState] = useState({
    key: 'itemDescription',
    dir: 'asc',
  });

  const pageSizeOptions = [
    { value: 10, label: '10' },
    { value: 20, label: '20' },
    { value: 50, label: '50' },
    { value: 100, label: '100' },
    { value: 250, label: '250' },
    { value: 500, label: '500' },
    { value: 1000, label: '1000' },
  ];

  const fetchBranches = async () => {
    try {
      const apiService = new ApiService();
      const res = await apiService.get('GET_BRANCH');
      const list = res?.data?.result || [];

      setBranches(
        list.map((b) => ({
          value: b.id,
          label: b.branchName,
        })),
      );
    } catch (error) {
      console.error('Branch Load Error:', error);
    }
  };

  const fetchData = async (page = 1, size = pagination.pageSize) => {
    setLoading(true);
    setErr('');

    try {
      const apiService = new ApiService();
      const params = new URLSearchParams();

      params.append('PageNo', page);
      params.append('PageSize', size);
      params.append('StartDate', formData.fromDate);
      params.append('EndDate', formData.toDate);
      params.append('BranchId', selectedBranch?.value || 0);
      // @Res_Branch_Id — branch filter for stored procedure (0 = all branches)
      params.append('Res_Branch_Id', selectedBranch?.value || 0);

      const res = await apiService.getApiParamater('GET_UNSOLD_ITEMS', `?${params.toString()}`);

      const result = Array.isArray(res?.data?.result) ? res.data.result : [];
      const apiPg = res?.data?.paginationData || {};

      const totalRecords = Number.isFinite(+apiPg.totalRecords) && +apiPg.totalRecords >= 0 ? +apiPg.totalRecords : result.length;

      const pageSizeNorm = Number.isFinite(+apiPg.pageSize) && +apiPg.pageSize > 0 ? +apiPg.pageSize : size;

      const totalPages = Number.isFinite(+apiPg.totalPages) && +apiPg.totalPages > 0 ? +apiPg.totalPages : Math.max(1, Math.ceil(totalRecords / pageSizeNorm));

      const currentPageNorm = Number.isFinite(+apiPg.currentPage) && +apiPg.currentPage > 0 ? +apiPg.currentPage : page;

      const hasPrevious = typeof apiPg.hasPrevious === 'boolean' ? apiPg.hasPrevious : currentPageNorm > 1;

      const hasNext = typeof apiPg.hasNext === 'boolean' ? apiPg.hasNext : currentPageNorm < totalPages;

      setRows(result);
      setPagination({
        currentPage: currentPageNorm,
        pageSize: pageSizeNorm,
        totalRecords,
        totalPages,
        hasPrevious,
        hasNext,
      });
    } catch (error) {
      console.error(error);
      setErr('Failed to fetch unsold report. Please try again.');
      setRows([]);
      setPagination((p) => ({
        ...p,
        totalRecords: 0,
        totalPages: 1,
        hasPrevious: false,
        hasNext: false,
      }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
    fetchData(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredRows = useMemo(() => {
    let data = [...rows];

    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      data = data.filter(
        (x) =>
          String(x.itemNo || '')
            .toLowerCase()
            .includes(q) ||
          String(x.itemDescription || '')
            .toLowerCase()
            .includes(q),
      );
    }

    return data;
  }, [rows, searchText]);

  const sortedRows = useMemo(() => {
    const { key, dir } = sortState;
    const copy = [...filteredRows];

    copy.sort((a, b) => {
      const av = a?.[key];
      const bv = b?.[key];

      const na = Number(av);
      const nb = Number(bv);

      if (Number.isFinite(na) && Number.isFinite(nb)) {
        return dir === 'asc' ? na - nb : nb - na;
      }

      return dir === 'asc' ? String(av ?? '').localeCompare(String(bv ?? '')) : String(bv ?? '').localeCompare(String(av ?? ''));
    });

    return copy;
  }, [filteredRows, sortState]);

  const totals = useMemo(() => {
    const totalUnsoldItems = sortedRows.length;
    const totalSoldQty = sortedRows.reduce((sum, r) => sum + (+r.soldQty || 0), 0);

    return { totalUnsoldItems, totalSoldQty };
  }, [sortedRows]);

  const showingFrom = pagination.totalRecords === 0 ? 0 : (pagination.currentPage - 1) * pagination.pageSize + 1;
  const showingTo = pagination.totalRecords === 0 ? 0 : Math.min(pagination.currentPage * pagination.pageSize, pagination.totalRecords);

  const onChangeRange = (e) => setFormData((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSearch = () => fetchData(1, pagination.pageSize);

  const onPrevPage = () => {
    if (loading || !pagination.hasPrevious) return;
    fetchData(pagination.currentPage - 1, pagination.pageSize);
  };

  const onNextPage = () => {
    if (loading || !pagination.hasNext) return;
    fetchData(pagination.currentPage + 1, pagination.pageSize);
  };

  const onPageSize = (opt) => {
    const newSize = opt?.value || 20;
    fetchData(1, newSize);
  };

  const onSort = (key) => {
    setSortState((prev) => (!prev || prev.key !== key ? { key, dir: 'asc' } : { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }));
  };

  const exportCSV = () => {
    const headers = COLUMNS.map((c) => `"${c.label}"`).join(',');
    const body = sortedRows.map((r) => COLUMNS.map((c) => `"${String(r?.[c.key] ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');

    const csv = `${headers}\n${body}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `unsold-items-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportExcel = () => {
    if (!sortedRows.length) return;

    const header = COLUMNS.map((c) => c.label);

    const body = sortedRows.map((r) => [r.itemNo || '', r.itemDescription || '', Number(r.soldQty) || 0]);

    const footer1 = ['Items (this page)', '', totals.totalUnsoldItems];
    const footer2 = ['Sold Qty (this page)', '', totals.totalSoldQty];

    const aoa = [header, ...body, [], footer1, footer2];
    const ws = XLSX.utils.aoa_to_sheet(aoa);

    ws['!cols'] = [{ wch: 18 }, { wch: 40 }, { wch: 12 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Unsold Items');

    const branchName = selectedBranch?.label ? selectedBranch.label.replace(/[\\/:*?"<>|]/g, '-') : 'All-Branches';

    XLSX.writeFile(wb, `unsold-items_${branchName}_${formData.fromDate}_to_${formData.toDate}.xlsx`);
  };

  return (
    <div className="p-4">
      <button className="addItem-btn mb-3" onClick={() => navigate(-1)}>
        ← Back
      </button>

      <div className="card border-0 shadow-sm rounded-4 mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-12 col-md-3">
              <label className="form-label fw-semibold">Select Branch</label>
              <Select
                value={selectedBranch}
                onChange={setSelectedBranch}
                options={branches}
                placeholder="Select Branch"
                isClearable
                menuPortalTarget={document.body}
                styles={selectStyles}
              />
            </div>

            <div className="col-12 col-md-3">
              <label className="form-label fw-semibold">
                {t('From')} {t('Date')}
              </label>
              <input type="date" className="form-control rounded-3" name="fromDate" value={formData.fromDate} onChange={onChangeRange} />
            </div>

            <div className="col-12 col-md-3">
              <label className="form-label fw-semibold">
                {t('To')} {t('Date')}
              </label>
              <input type="date" className="form-control rounded-3" name="toDate" value={formData.toDate} onChange={onChangeRange} />
            </div>

            <div className="col-12 col-md-3">
              <label className="form-label fw-semibold">Search Item</label>
              <input
                type="text"
                className="form-control rounded-3"
                placeholder="Search by item no or description"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>

            <div className="col-12 d-flex flex-wrap gap-2 mt-4 align-items-center">
              <button className="btn btn-primary px-4 rounded-3" onClick={onSearch} disabled={loading}>
                {loading ? 'Loading…' : 'Search'}
              </button>

              <button className="btn btn-outline-secondary rounded-3" onClick={exportCSV} disabled={!sortedRows.length}>
                Export CSV
              </button>

              <button className="btn btn-outline-success rounded-3" onClick={exportExcel} disabled={!sortedRows.length}>
                Export Excel
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* {sortedRows.length > 0 && (
        <div className="row g-3 mb-3">
          <div className="col-12 col-md-6">
            <div className="card border-0 shadow-sm rounded-4 h-100">
              <div className="card-body">
                <div className="text-muted small mb-1">Total Unsold Items</div>
                <div className="fs-3 fw-bold">{intFmt(totals.totalUnsoldItems)}</div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6">
            <div className="card border-0 shadow-sm rounded-4 h-100">
              <div className="card-body">
                <div className="text-muted small mb-1">Total Sold Qty</div>
                <div className="fs-3 fw-bold">{intFmt(totals.totalSoldQty)}</div>
              </div>
            </div>
          </div>
        </div>
      )} */}

      <div className="POsgridshad rounded-4 p-3 bg-white shadow-sm">
        {err && <div className="alert alert-danger m-2">{err}</div>}

        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-3">
          <div className="text-muted small">
            Showing <b>{intFmt(showingFrom)}</b>–<b>{intFmt(showingTo)}</b> of <b>{intFmt(pagination.totalRecords)}</b>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <label className="text-muted small mb-0">Page size</label>
            <div style={{ minWidth: 130 }}>
              <Select
                value={pageSizeOptions.find((o) => o.value === pagination.pageSize)}
                onChange={onPageSize}
                options={pageSizeOptions}
                isSearchable={false}
                placeholder="Page size"
                styles={selectStyles}
                isDisabled={loading}
              />
            </div>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onPrevPage} disabled={loading || !pagination.hasPrevious}>
              Prev
            </button>
            <span className="text-muted small">
              Page <b>{pagination.currentPage}</b> / <b>{pagination.totalPages}</b>
            </span>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onNextPage} disabled={loading || !pagination.hasNext}>
              Next
            </button>
          </div>
        </div>

        <div className="custom-table-container table-responsive" style={{ maxHeight: '65vh', overflow: 'auto' }}>
          <table className="custom-table table align-middle mb-0">
            <thead className="bg-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
              <tr>
                {COLUMNS.map((c) => {
                  const sorted = sortState?.key === c.key;
                  return (
                    <th
                      key={c.key}
                      onClick={() => onSort(c.key)}
                      className={`${c.align === 'right' ? 'text-end' : ''} ${sorted ? 'table-active' : ''}`}
                      style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >
                      {c.label}
                      {sorted ? (sortState.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {loading && (
                <tr>
                  <td colSpan={COLUMNS.length} className="text-center py-4">
                    Loading…
                  </td>
                </tr>
              )}

              {!loading && sortedRows.length === 0 && (
                <tr>
                  <td colSpan={COLUMNS.length} className="text-center text-muted py-4">
                    No data found
                  </td>
                </tr>
              )}

              {!loading &&
                sortedRows.map((r, index) => (
                  <tr key={r.recordId || `${r.itemNo}-${index}`}>
                    <td>{r.itemNo || '—'}</td>
                    <td>{r.itemDescription || '—'}</td>
                    <td className="text-end">{intFmt(r.soldQty)}</td>
                  </tr>
                ))}
            </tbody>

            {sortedRows.length > 0 && (
              <tfoot>
                <tr className="bg-light fw-bold">
                  <td colSpan={2}>Items (this page)</td>
                  <td className="text-end">{intFmt(totals.totalUnsoldItems)}</td>
                </tr>
                <tr className="fw-bold">
                  <td colSpan={2}>Sold Qty (this page)</td>
                  <td className="text-end">{intFmt(totals.totalSoldQty)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        <div className="d-flex justify-content-end align-items-center gap-2 pt-3 mt-2 border-top flex-wrap">
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onPrevPage} disabled={loading || !pagination.hasPrevious}>
            Prev
          </button>
          <span className="text-muted small">
            Page <b>{pagination.currentPage}</b> / <b>{pagination.totalPages}</b>
          </span>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onNextPage} disabled={loading || !pagination.hasNext}>
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

const mapStatetoProps = (state) => state;
export default connect(mapStatetoProps)(UnSoldItemReport);
